import { useState, useEffect } from "react";
import { BrandLogo } from "@/components/BrandLogo";

interface AuthLayoutProps {
  children: React.ReactNode;
  subtitle?: string;
  valuePanel?: React.ReactNode;
  taglines?: string[];
}

const AuthLayout = ({ children, subtitle, valuePanel, taglines }: AuthLayoutProps) => {
  const [taglineIndex, setTaglineIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (!taglines || taglines.length <= 1) return;

    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setTaglineIndex(i => (i + 1) % taglines.length);
        setVisible(true);
      }, 400);
    }, 4000);

    return () => clearInterval(interval);
  }, [taglines]);

  return (
    <div className="relative min-h-screen w-full flex">
      {/* Background image with dark overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url(/auth-bg.jpg)" }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-forest/85 via-forest/90 to-forest/95" />

      {/* Value panel — left side, hidden on mobile */}
      {valuePanel && (
        <div className="relative z-10 hidden lg:flex w-1/2 items-center justify-center p-12">
          {valuePanel}
        </div>
      )}

      {/* Card side */}
      <div
        className={`relative z-10 flex flex-1 items-center justify-center p-4 ${valuePanel ? "lg:w-1/2" : ""}`}
      >
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center">
            <h1>
              <BrandLogo surface="dark" className="mx-auto h-11 w-auto" />
            </h1>
            {subtitle && <p className="text-sm text-white/70 mt-2">{subtitle}</p>}
            {taglines && taglines.length > 0 && (
              <p
                className={`text-sm text-white/60 mt-3 h-5 transition-opacity duration-300 ${visible ? "opacity-100" : "opacity-0"}`}
              >
                {taglines[taglineIndex]}
              </p>
            )}
          </div>
          <div className="bg-background/90 backdrop-blur-xl rounded-lg border shadow-2xl">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
